import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { YAxisType } from 'klinecharts';
import { SymbolSearch } from './SymbolSearch';
import { CompareBar } from './CompareBar';
import { CandleTypeSelector } from './CandleTypeSelector';
import { AdjustmentSelector } from './AdjustmentSelector';
import { HeaderPopover } from './HeaderPopover';
import { TimeframeBar } from '@/components/Chart/TimeframeBar';
import { IndicatorModal } from '@/components/Chart/IndicatorModal';
import { ChartSettingsModal } from '@/components/Chart/ChartSettingsModal';
import { UserAvatar } from '@/components/Auth/UserAvatar';
import { SocialLinks } from '@/components/ui/SocialLinks';
import { useChartStore } from '@/store/chartStore';
import { useAuthStore } from '@/store/authStore';
import { useMacroStore } from '@/store/macroStore';
import { MacroPickerModal } from '@/components/Macro/MacroPickerModal';
import {
  SunIcon, MoonIcon, HomeIcon,
  MenuIcon, MenuRightIcon, IndicatorIcon, CameraIcon, ExpandIcon, CompressIcon, GearIcon,
  SparklesIcon, LogOutIcon, ChevronRightIcon, ChevronDownIcon, ResetViewIcon, ShareIcon, CheckIcon,
} from '@/components/icons';
import { trackUIAction, trackAuthAction, trackUpgradeModalOpen, trackMacroPickerOpen } from '@/services/gtag';
import { captureChartScreenshot, captureChartPreview } from '@/utils/screenshotCanvas';
import { createShareLink } from '@/services/shareChart';
import { buildCurrentTemplateSnapshot } from '@/components/Chart/templateSnapshot';

// Nearly all of this component's JS goes unused on a normal page view — only
// fetch it once the user actually opens profile settings.
const ProfileSettingsModal = lazy(() =>
  import('@/components/Auth/ProfileSettingsModal').then((m) => ({ default: m.ProfileSettingsModal })),
);

export function Header() {
  const theme              = useChartStore((s) => s.theme);
  const toggleTheme        = useChartStore((s) => s.toggleTheme);
  const chart              = useChartStore((s) => s.chart);
  const chartWrapperEl     = useChartStore((s) => s.chartWrapperEl);
  const drawerCollapsed    = useChartStore((s) => s.drawingToolbarCollapsed);
  const setDrawerCollapsed = useChartStore((s) => s.setDrawingToolbarCollapsed);
  const rightToolbarOpen    = useChartStore((s) => s.rightToolbarOpen);
  const toggleRightToolbar  = useChartStore((s) => s.toggleRightToolbar);
  const compareSymbols      = useChartStore((s) => s.compareSymbols);

  const user                = useAuthStore((s) => s.user);
  const logout              = useAuthStore((s) => s.logout);
  const openUpgradeModal    = useAuthStore((s) => s.openUpgradeModal);
  const openProfileSettings = useAuthStore((s) => s.openProfileSettings);
  const profileSettingsOpen = useAuthStore((s) => s.profileSettingsOpen);

  const navigate = useNavigate();

  const [indicatorOpen,  setIndicatorOpen]  = useState(false);
  const [macroPickerOpen, setMacroPickerOpen] = useState(false);
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [profileOpen,    setProfileOpen]    = useState(false);
  const [fullScreen,     setFullScreen]     = useState(false);
  const [shareState,     setShareState]     = useState<'idle' | 'busy' | 'copied' | 'error'>('idle');

  const profileRef = useRef<HTMLDivElement>(null);
  const profilePanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = () => setFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!profileOpen) return;
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (profileRef.current?.contains(t)) return;
      if (profilePanelRef.current?.contains(t)) return;
      setProfileOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [profileOpen]);

  function handleResetView() {
    if (!chart) return;
    // Reset X axis zoom to default bar spacing (8px per bar)
    chart.setBarSpace(8);
    // Restore Y axis auto-scale (internally calls setAutoCalcTickFlag(true))
    chart.setStyles({ yAxis: { type: compareSymbols.length > 0 ? YAxisType.Percentage : YAxisType.Normal } });
    // Scroll back to the latest candle
    chart.scrollToRealTime(300);
    trackUIAction('reset_view');
  }

  async function handleScreenshot() {
    if (!chart || !chartWrapperEl) return;
    await captureChartScreenshot(chart, chartWrapperEl);
    trackUIAction('screenshot');
  }

  async function handleShare() {
    if (!chart || shareState === 'busy') return;
    setShareState('busy');
    try {
      const { symbol, timeframe } = useChartStore.getState();
      const snapshot = buildCurrentTemplateSnapshot(chart, { includeDrawings: true });
      // A missing or slow preview must never stop the link from being created.
      const preview = chartWrapperEl
        ? await Promise.race([
            captureChartPreview(chart, chartWrapperEl),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
          ])
        : null;
      const url = await createShareLink(symbol, timeframe, snapshot, preview);
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Clipboard writes can be refused after the async round-trip (e.g. Safari) — let the user copy it manually.
        window.prompt('Copy this link to share your chart', url);
      }
      trackUIAction('share_chart');
      setShareState('copied');
    } catch {
      setShareState('error');
    }
    setTimeout(() => setShareState('idle'), 2500);
  }

  function handleFullscreen() {
    if (!fullScreen) {
      document.documentElement.requestFullscreen?.();
      trackUIAction('fullscreen', 'enter');
    } else {
      document.exitFullscreen?.();
      trackUIAction('fullscreen', 'exit');
    }
  }

  return (
    <>
      <header className="h-12 px-3 glass-header shrink-0 overflow-visible">
      <div className="flex items-center gap-1 md:gap-1.5 h-full overflow-x-auto scrollbar-none">

        {/* Drawing toolbar toggle — desktop only; mobile has the same control
            in the bottom action bar (ChartActionBar's Draw button), so keeping
            this one too would just duplicate it and eat header space. */}
        <button
          onClick={() => setDrawerCollapsed(!drawerCollapsed)}
          title={drawerCollapsed ? 'Show Drawing Tools' : 'Hide Drawing Tools'}
          className="hidden md:flex p-1.5 rounded hover:bg-panel-hover text-muted hover:text-text transition-colors shrink-0"
        >
          <MenuIcon size={22} />
        </button>

        {/* Back to the home page — left of the symbol search, on desktop and mobile */}
        <Link
          to="/"
          title="CeylonCharts home"
          aria-label="CeylonCharts home"
          className="flex p-0.5 md:p-1.5 rounded hover:bg-panel-hover text-muted hover:text-text transition-colors shrink-0"
        >
          <HomeIcon size={22} />
        </Link>

        {/* Symbol search */}
        <SymbolSearch />

        {/* Compare symbols — inline on mobile (circle button), labeled on desktop */}
        <CompareBar />

        {/* Macro — mobile-only compact icon, placed next to Compare. Desktop keeps
            its labeled version further along the header (see below). */}
        <button
          onClick={() => {
            if (!user) { navigate('/login'); return; }
            trackMacroPickerOpen('header');
            setMacroPickerOpen(true);
          }}
          title="Macro indicators"
          className={`md:hidden flex items-center justify-center p-1 rounded transition-colors shrink-0 ${
            useMacroStore.getState().pinnedSeries.length > 0
              ? 'text-accent'
              : 'text-muted hover:text-accent'
          }`}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
            <path d="M2 12h20" />
          </svg>
        </button>

        {/* Timeframe tabs — desktop only (mobile gets its own sub-row below) */}
        <div className="hidden md:flex items-center gap-1.5">
          <div className="w-px h-4 bg-border mx-1 shrink-0" />
          <TimeframeBar />
          <div className="w-px h-4 bg-border mx-1 shrink-0" />
        </div>

        {/* Candle type — desktop only */}
        <div className="hidden md:flex">
          <CandleTypeSelector />
        </div>

        {/* Indicator — desktop only */}
        <button
          onClick={() => setIndicatorOpen((v) => !v)}
          title="Indicator"
          className={`hidden md:flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
            indicatorOpen
              ? 'bg-accent/20 text-accent'
              : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          <IndicatorIcon size={22} />
          <span className="hidden sm:inline">Indicator</span>
        </button>

        {/* Macro — desktop only */}
        <button
          onClick={() => {
            if (!user) {
              navigate('/login');
              return;
            }
            trackMacroPickerOpen('header');
            setMacroPickerOpen(true);
          }}
          title="Macro indicators"
          className={`hidden md:flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
            useMacroStore.getState().pinnedSeries.length > 0
              ? 'bg-accent/20 text-accent'
              : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
            <path d="M2 12h20" />
          </svg>
          <span className="hidden sm:inline">Macro</span>
        </button>

        <div className="flex-1" />

        {/* Price adjustment (splits / dividends / scrip dividends / rights) */}
        <AdjustmentSelector />

        {/* Share — desktop only. Saves symbol + indicators + drawings and copies a link. */}
        <button
          onClick={handleShare}
          disabled={!chart || shareState === 'busy'}
          title={
            shareState === 'copied' ? 'Link copied!'
            : shareState === 'error' ? 'Could not create link — try again'
            : 'Share chart'
          }
          className={`hidden md:flex items-center gap-1 p-1.5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            shareState === 'error'
              ? 'text-red-500'
              : shareState === 'copied'
                ? 'text-accent'
                : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          {shareState === 'copied' ? <CheckIcon size={22} /> : <ShareIcon size={22} />}
          {shareState === 'copied' && <span className="text-sm">Link copied</span>}
          {shareState === 'error' && <span className="text-sm">Failed</span>}
        </button>

        {/* Screenshot — desktop only */}
        <button
          onClick={handleScreenshot}
          disabled={!chart}
          title="Screenshot"
          className="hidden md:flex p-1.5 rounded text-muted hover:text-text hover:bg-panel-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <CameraIcon size={22} />
        </button>

        {/* Fullscreen — desktop only */}
        <button
          onClick={handleFullscreen}
          title={fullScreen ? 'Exit Full Screen' : 'Full Screen'}
          className="hidden md:flex p-1.5 rounded text-muted hover:text-text hover:bg-panel-hover transition-colors"
        >
          {fullScreen ? <CompressIcon size={22} /> : <ExpandIcon size={22} />}
        </button>

        {/* Reset chart view */}
        <button
          onClick={handleResetView}
          disabled={!chart}
          title="Reset view"
          className="p-1.5 rounded hover:bg-panel-hover text-muted hover:text-text transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ResetViewIcon size={22} />
        </button>

        {/* Right toolbar toggle — desktop only (mobile uses bottom nav). Grouped with the other view/layout
            controls rather than next to Account — it toggles a panel, not a preference or identity control. */}
        <button
          onClick={toggleRightToolbar}
          title={rightToolbarOpen ? 'Hide right panel' : 'Show right panel'}
          className={`hidden md:flex p-1.5 rounded transition-colors ${
            rightToolbarOpen
              ? 'text-accent bg-accent/10'
              : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          <MenuRightIcon size={22} />
        </button>

        <div className="w-px h-4 bg-border mx-0 md:mx-1 shrink-0" />

        {/* Settings */}
        <button
          onClick={() => setSettingsOpen((v) => !v)}
          title="Setting"
          className={`flex p-1.5 rounded transition-colors ${
            settingsOpen
              ? 'bg-accent/20 text-accent'
              : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          <GearIcon size={22} />
        </button>

        {/* Theme toggle — desktop only (mobile users can change theme in settings) */}
        <button
          onClick={() => { toggleTheme(); trackUIAction('theme_toggle', theme === 'dark' ? 'light' : 'dark'); }}
          title="Toggle theme"
          className="hidden md:block p-1.5 rounded hover:bg-panel-hover text-muted hover:text-text transition-colors"
        >
          {theme === 'dark' ? <SunIcon size={22} /> : <MoonIcon size={22} />}
        </button>

        <div className="w-px h-4 bg-border mx-0 md:mx-1 shrink-0" />

        {/* Login / User avatar — anchored at the far right, grouped as a pill so it reads as the account control, not another utility icon */}
        {user ? (
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen((v) => !v)}
              title="Account"
              className={`flex items-center gap-1 pl-1 pr-1.5 py-1 rounded-full transition-colors ${
                profileOpen
                  ? 'bg-accent/20 ring-1 ring-accent/40'
                  : 'bg-panel-hover/70 hover:bg-panel-hover ring-1 ring-border'
              }`}
            >
              <UserAvatar user={user} size={26} />
              <ChevronDownIcon size={12} className={`text-muted shrink-0 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
            </button>

            <HeaderPopover
              ref={profilePanelRef}
              anchorRef={profileRef}
              open={profileOpen}
              align="right"
              className="mt-2 w-64 glass-modal rounded-2xl z-dropdown overflow-hidden animate-menu-enter"
            >

                {/* Identity card */}
                <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-border">
                  <UserAvatar user={user} size={40} />
                  <div className="min-w-0 flex-1">
                    {user.name && <p className="text-sm font-semibold text-modal-text truncate">{user.name}</p>}
                    <p className="text-xs text-modal-muted truncate">{user.email}</p>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-semibold ${
                      user.plan === 'pro'      ? 'bg-amber-500/20 text-amber-400' :
                      user.plan === 'business' ? 'bg-purple-500/20 text-purple-300' :
                                                'bg-border text-modal-muted'
                    }`}>
                      {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                    </span>
                    {user.planExpiresAt && user.plan !== 'basic' && (
                      <span className="text-xs text-modal-muted ml-1.5">
                        · expires {new Date(user.planExpiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Email verification warning */}
                {!user.emailVerified && (
                  <div className="mx-3 mt-3 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-500">Verify your email to access all features.</p>
                  </div>
                )}

                {/* Upgrade CTA card */}
                {user.plan !== 'business' && (
                  <div className="px-3 pt-3">
                    <button
                      onClick={() => { setProfileOpen(false); trackUpgradeModalOpen('header'); openUpgradeModal(); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all"
                      style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.25)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.5)'; e.currentTarget.style.background = 'rgba(245,158,11,0.13)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.25)'; e.currentTarget.style.background = 'rgba(245,158,11,0.08)'; }}
                    >
                      <SparklesIcon size={16} className="text-amber-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-amber-400 leading-none mb-0.5">Pro free for 30 days</p>
                        <p className="text-xs text-modal-muted">Unlock unlimited features</p>
                      </div>
                      <ChevronRightIcon size={14} className="ml-auto text-amber-400/50 shrink-0" />
                    </button>
                  </div>
                )}

                {/* Settings */}
                <div className="px-2 pt-2">
                  <button
                    onClick={() => { setProfileOpen(false); openProfileSettings(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-modal-muted hover:text-modal-text hover:bg-panel-hover rounded-lg transition-colors"
                  >
                    <GearIcon size={14} />
                    Settings
                  </button>
                </div>

                {/* Sign out */}
                <div className="px-2 py-2">
                  <button
                    onClick={() => { setProfileOpen(false); trackAuthAction('logout', user?.plan); logout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-modal-muted hover:text-red-400 hover:bg-red-500/5 rounded-lg transition-colors"
                  >
                    <LogOutIcon size={14} />
                    Sign out
                  </button>
                </div>

                {/* Legal links */}
                <div className="px-4 pt-3 pb-2.5 border-t border-border flex items-center gap-3">
                  <Link
                    to="/privacy"
                    onClick={() => setProfileOpen(false)}
                    className="text-sm text-modal-muted hover:text-modal-text transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-sm text-modal-muted/40">·</span>
                  <Link
                    to="/terms"
                    onClick={() => setProfileOpen(false)}
                    className="text-sm text-modal-muted hover:text-modal-text transition-colors"
                  >
                    Terms
                  </Link>
                </div>

                {/* Social links */}
                <div className="px-4 pb-4">
                  <SocialLinks size={19} />
                </div>
            </HeaderPopover>
          </div>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="px-3 md:px-3.5 py-1.5 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors shrink-0"
          >
            Login
          </button>
        )}
      </div>
      </header>

      {indicatorOpen && <IndicatorModal onClose={() => setIndicatorOpen(false)} />}
      {macroPickerOpen && <MacroPickerModal onClose={() => setMacroPickerOpen(false)} />}
      {settingsOpen  && <ChartSettingsModal onClose={() => setSettingsOpen(false)} />}
      {profileSettingsOpen && (
        <Suspense fallback={null}>
          <ProfileSettingsModal />
        </Suspense>
      )}
    </>
  );
}

